import React, { useEffect, useMemo, useState } from 'react';
import { get } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { toastr } from 'react-redux-toastr';
import { useQueryClient } from 'react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Banner,
  Box,
  Select,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  Modal,
  SingleSelect,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  ModalConfirm,
  useConfirmModal,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  AlertInfoIcon,
  FloppyIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import useIntl from '../../../hooks/useIntl';
import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import naurMessages from '../../../components/NaurWarningWrapper/messages';
import { calculateActualIncomeAverage } from '../../Home/PropertyComplianceTab/SetAsideSummary/utils';
import { getAffordableFloorPlanProgramTypes } from '../../../utils/affordable';
import { moveOutDate as getMoveOutDate } from '../../../utils/unit-helpers';

import messages from './messages';
import appMessages from '../../App/messages';
import {
  useChangeFloorPlanNaurEvents,
  useFloorPlansQuery,
  useHudContractOptionsQuery,
  useUnitFloorPlanScheduleQuery,
  useUpdateUnitMutation,
} from './hooks';

import type {
  ChangeFloorPlanFormValues,
  ChangeFloorPlanModalProps,
} from './types';

const getFloorPlanOptionText = ({ internalName, marketingName }) =>
  `${internalName} "${marketingName}"`;

const ChangeFloorPlanModal = ({
  open,
  unit,
  propertyId,
  organizationId,
  onClose,
  onSuccess,
  isAffordableMixedProperty,
  isUnitCommercial,
  setAsideSummaryKPI,
  incomeAveragingPercent,
}: ChangeFloorPlanModalProps): React$Node => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { openConfirm, openConfirmModal, closeConfirmModal } =
    useConfirmModal();
  const [projectedIncomeAvg, setProjectedIncomeAvg] = useState(null);
  const unitId = get(unit, 'id', '');
  const currentFloorPlanId = get(unit, 'floorPlanId', '');
  const isVacant = get(unit, 'unitStatus.isVacant', false);
  // ENG-28606 AC #3: when occupied-units-schedule-for-later is OFF, the
  // occupied-only scheduling restriction must not be enforced. Treat
  // occupied units like vacant ones — immediate change, no NTV gate.
  const { occupiedUnitsScheduleForLater } = useFlags();
  const enforceOccupiedScheduling = Boolean(occupiedUnitsScheduleForLater);
  const unitStatusDescription = get(unit, 'unitStatus.description', '');
  const buildingId = get(unit, 'buildingId', '');
  const currentUnitBedroomCount = get(unit, 'floorPlan.nBeds', 0);

  const noticeToVacate = get(
    unit,
    'currentAssignation.lease.noticeToVacate',
    null,
  );
  const hasNoticeToVacate = Boolean(noticeToVacate);
  const scheduledMoveOutDate = getMoveOutDate(unit).value;

  const { data: schedule } = useUnitFloorPlanScheduleQuery(
    unitId,
    propertyId,
    organizationId,
    {
      enabled: Boolean(unitId && propertyId && organizationId),
    },
  );

  const { data: floorPlans = [], isLoading: isFloorPlansLoading } =
    useFloorPlansQuery(propertyId, organizationId, currentFloorPlanId, {
      enabled: Boolean(open && propertyId && organizationId),
    });

  const reactHookFormProps = useReactHookFormProps({
    defaultValues: {
      floorPlanId: schedule?.scheduledFloorPlanId || currentFloorPlanId || null,
      naurEventId: null,
      hudSubsidyTypeId: unit?.hudSubsidyTypeId ?? null,
    },
    mode: 'onChange',
  });

  const {
    Controller,
    handleSubmit,
    isDirty,
    reset,
    watch,
    setValue,
    getValues,
  } = reactHookFormProps;

  const selectedFloorPlanId = watch('floorPlanId');
  const selectedNaurEventId = watch('naurEventId');
  const selectedHudSubsidyTypeId = watch('hudSubsidyTypeId');
  const isCurrentFloorPlanSelected = selectedFloorPlanId === currentFloorPlanId;

  const selectedFloorPlan = useMemo(
    () => floorPlans.find((fp) => fp.id === selectedFloorPlanId) ?? null,
    [floorPlans, selectedFloorPlanId],
  );

  const isHUDFloorPlan = useMemo(() => {
    // Use the loaded floor plan when available; fall back to unit.floorPlan so the
    // field and its query are ready immediately on modal open (before the floor
    // plans query resolves).
    const planToCheck = selectedFloorPlan ?? unit?.floorPlan ?? null;
    return planToCheck
      ? getAffordableFloorPlanProgramTypes(planToCheck).isHUDFloorPlan
      : false;
  }, [selectedFloorPlan, unit?.floorPlan]);

  const { data: hudContractData } = useHudContractOptionsQuery(
    unitId,
    propertyId,
    organizationId,
    selectedFloorPlanId ?? null,
    {
      enabled: Boolean(
        open && unitId && propertyId && organizationId && isHUDFloorPlan,
      ),
    },
  );
  const hudOptions = useMemo(
    () => hudContractData?.options ?? [],
    [hudContractData],
  );
  const selectedAffordableFundingId =
    hudContractData?.selectedAffordableFundingId ?? null;

  const {
    isLockedByNaur,
    isBuildingNaurEventsLoading,
    showNaurField,
    naurEventOptions,
    hasNaurAssociationChanged,
    hasSelectableEvents,
    currentlyAssociatedNaurEvent,
  } = useChangeFloorPlanNaurEvents({
    open,
    unitId,
    buildingId,
    currentFloorPlanId,
    selectedFloorPlanId,
    selectedNaurEventId,
    currentUnitBedroomCount,
    floorPlans,
    intl,
  });

  const initialNaurEventId = currentlyAssociatedNaurEvent?.id ?? null;

  const defaultValues: ChangeFloorPlanFormValues = useMemo(() => {
    const hudSubsidyTypeId = unit?.hudSubsidyTypeId ?? null;

    if (schedule?.scheduledFloorPlanId) {
      return {
        floorPlanId: schedule.scheduledFloorPlanId,
        naurEventId: initialNaurEventId,
        hudSubsidyTypeId,
      };
    }

    return {
      floorPlanId: currentFloorPlanId || null,
      naurEventId: initialNaurEventId,
      hudSubsidyTypeId,
    };
  }, [schedule, currentFloorPlanId, initialNaurEventId, unit]);

  useEffect(() => {
    if (open) {
      const currentHud = getValues('hudSubsidyTypeId');
      reset({
        ...defaultValues,
        ...(currentHud != null ? { hudSubsidyTypeId: currentHud } : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultValues]);

  const updateUnitMutation = useUpdateUnitMutation({
    propertyId,
    organizationId,
    unitId,
  });
  const isScheduleActive = Boolean(schedule?.scheduledFloorPlanId);
  const isClearSchedule =
    isScheduleActive &&
    (selectedFloorPlanId === null || selectedFloorPlanId === '');
  const hasFloorPlanSelection = Boolean(selectedFloorPlanId);

  const isFloorPlanChanging =
    hasFloorPlanSelection && !isCurrentFloorPlanSelected;

  // Single source of truth for what the conversion will do — drives both the
  // banner message and the Save gate. Timing is auto-determined by occupancy
  // when ENG-28606 occupied-units-schedule-for-later flag is ON; when OFF,
  // every change applies immediately regardless of occupancy.
  const conversionState = useMemo(() => {
    if (!isFloorPlanChanging) return null;
    if (isVacant || !enforceOccupiedScheduling) {
      return {
        status: 'info',
        blocked: false,
        message: intl.formatMessage(messages.bannerImmediate),
      };
    }
    if (!hasNoticeToVacate) {
      return {
        status: 'warning',
        blocked: true,
        message: intl.formatMessage(messages.errorNoNtv),
      };
    }
    if (!scheduledMoveOutDate) {
      return {
        status: 'warning',
        blocked: true,
        message: intl.formatMessage(messages.errorNoMoveOutDate),
      };
    }
    return {
      status: 'info',
      blocked: false,
      message: intl.formatMessage(messages.bannerScheduled, {
        date: scheduledMoveOutDate,
      }),
    };
  }, [
    intl,
    isFloorPlanChanging,
    isVacant,
    enforceOccupiedScheduling,
    hasNoticeToVacate,
    scheduledMoveOutDate,
  ]);
  const isConversionBlocked = Boolean(conversionState?.blocked);

  const hasHudSubsidyChanged = useMemo(
    () =>
      isHUDFloorPlan &&
      isDirty &&
      Boolean(selectedHudSubsidyTypeId) &&
      selectedHudSubsidyTypeId !== (unit?.hudSubsidyTypeId ?? null),
    [isHUDFloorPlan, isDirty, selectedHudSubsidyTypeId, unit?.hudSubsidyTypeId],
  );

  const isSaveDisabled = useMemo(() => {
    if (isLockedByNaur) return true;

    // When a HUD floor plan is selected, a HUD Subsidy Type must be chosen.
    if (isHUDFloorPlan && !selectedHudSubsidyTypeId) return true;

    // Special case: Allow saving when only NAUR or HUD subsidy type changes (same floor plan)
    if (
      isCurrentFloorPlanSelected &&
      !isScheduleActive &&
      (hasNaurAssociationChanged || hasHudSubsidyChanged)
    ) {
      return false;
    }

    // Current floor plan selected with no schedule → disable (unless NAUR/HUD changed)
    if (isCurrentFloorPlanSelected && !isScheduleActive) {
      return true;
    }

    // Current floor plan selected with schedule → enable (to clear)
    if (isCurrentFloorPlanSelected && isScheduleActive && isDirty) {
      return false;
    }

    return (
      !isDirty ||
      isConversionBlocked ||
      (!hasFloorPlanSelection && !isClearSchedule)
    );
  }, [
    isLockedByNaur,
    isHUDFloorPlan,
    selectedHudSubsidyTypeId,
    isDirty,
    isCurrentFloorPlanSelected,
    isScheduleActive,
    isClearSchedule,
    isConversionBlocked,
    hasFloorPlanSelection,
    hasNaurAssociationChanged,
    hasHudSubsidyChanged,
  ]);

  const needsIncomeAverageConfirmation = (floorPlanId: string): boolean => {
    if (isUnitCommercial || !isAffordableMixedProperty || !setAsideSummaryKPI) {
      return false;
    }

    const allFloorPlans =
      queryClient.getQueryData([
        'floorPlans',
        organizationId,
        propertyId,
        currentFloorPlanId,
      ]) || [];
    const currentFloorPlan = allFloorPlans.find(
      (fp) => fp.id === currentFloorPlanId,
    );
    const currentRestriction = ((
      get(currentFloorPlan, 'unitRentRestriction', '') || ''
    ).match(/\d+/) || [])[0];

    const targetFloorPlan = allFloorPlans.find((fp) => fp.id === floorPlanId);
    const selectedRestriction = ((
      get(targetFloorPlan, 'unitRentRestriction', '') || ''
    ).match(/\d+/) || [])[0];

    if (currentRestriction === selectedRestriction) {
      return false;
    }

    const projectedKPI = cloneDeep(setAsideSummaryKPI);

    const previousProgram =
      currentRestriction == null
        ? undefined
        : projectedKPI.find((entry) => entry.setAside === currentRestriction);

    const selectedProgram =
      selectedRestriction == null
        ? undefined
        : projectedKPI.find((entry) => entry.setAside === selectedRestriction);

    if (previousProgram != null) {
      previousProgram.numActualUnits--;
    }

    if (selectedProgram != null) {
      selectedProgram.numActualUnits++;
    }

    setProjectedIncomeAvg(calculateActualIncomeAverage(projectedKPI) || 0);
    return true;
  };

  const handleSaveWithConfirmation = () => {
    if (
      !isClearSchedule &&
      selectedFloorPlanId &&
      needsIncomeAverageConfirmation(selectedFloorPlanId)
    ) {
      openConfirmModal();
      return;
    }
    handleSubmit(handleSave)();
  };

  const handleConfirmSave = () => {
    closeConfirmModal();
    handleSubmit(handleSave)();
  };

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  const invalidateGrossRentsQueries = () => {
    queryClient.invalidateQueries([
      'grossRentCertifications',
      organizationId,
      propertyId,
    ]);
    queryClient.invalidateQueries([
      'grossRentChanges',
      organizationId,
      propertyId,
    ]);
  };

  const handleSave = async (values: ChangeFloorPlanFormValues) => {
    if (!unitId) return;

    try {
      const isClearingSchedule = !values.floorPlanId && isScheduleActive;
      const isCancelingSchedule =
        isCurrentFloorPlanSelected && isScheduleActive;

      if (isClearingSchedule || isCancelingSchedule) {
        await updateUnitMutation.mutateAsync({
          floorPlanId: isCancelingSchedule ? currentFloorPlanId : null,
          clearSchedule: true,
        });
        queryClient.setQueryData(['unitFloorPlanSchedule', unitId], null);
        invalidateGrossRentsQueries();
        toastr.success(
          intl.formatMessage(appMessages.success),
          intl.formatMessage(messages.saveSuccessCanceled),
        );
        onSuccess();
        return;
      }

      // Special case: User is only changing NAUR association (same floor plan)
      // Send onlyNaurUpdate flag so backend can skip floor plan logic
      if (isCurrentFloorPlanSelected && hasNaurAssociationChanged) {
        await updateUnitMutation.mutateAsync({
          floorPlanId: currentFloorPlanId,
          naurEventId: values.naurEventId || null,
          onlyNaurUpdate: true,
        });

        // Invalidate NAUR caches to refresh data
        queryClient.invalidateQueries(['naurEvents', 'building', buildingId]);
        queryClient.invalidateQueries(['naurEventsForUnit', unitId]);

        toastr.success(
          intl.formatMessage(appMessages.success),
          intl.formatMessage(messages.naurAssociationUpdated),
        );
        onSuccess();
        return;
      }

      // Timing is determined by occupancy when the
      // occupied-units-schedule-for-later flag is ON; when OFF, the change
      // always applies immediately regardless of occupancy (ENG-28606 AC #3).
      const isScheduled = !isVacant && enforceOccupiedScheduling;

      // Build payload with optional naurEventId
      const payload = {
        floorPlanId: values.floorPlanId,
      };

      if (isHUDFloorPlan) {
        payload.hudSubsidyTypeId = values.hudSubsidyTypeId || null;
      }

      // Include naurEventId when NAUR field is shown.
      // Send it even if null/empty to support clearing associations (Scenario 7).
      // Backend will handle:
      // - If naurEventId is provided: associate unit with that NAUR event.
      // - If naurEventId is null/empty AND unit had previous association:
      //   clear the association and revert NAUR status to OPEN.
      let shouldInvalidateNaur = showNaurField;

      if (showNaurField) {
        payload.naurEventId = values.naurEventId || null;
      } else if (currentlyAssociatedNaurEvent && values.floorPlanId) {
        // Scenario 9.3 (LIHTC → Market Rate / non-LIHTC): the NAUR field is hidden,
        // but we must automatically clear the existing association so the backend
        // reverts the NAUR event to Open and nullifies the satisfyingUnitId.
        const selectedFP = floorPlans.find(
          (fp) => fp.id === values.floorPlanId,
        );
        if (selectedFP) {
          const { isLIHTCFloorPlan: isSelectedLIHTC } =
            getAffordableFloorPlanProgramTypes(selectedFP);
          if (!isSelectedLIHTC) {
            payload.naurEventId = null;
            shouldInvalidateNaur = true;
          }
        }
      }

      await updateUnitMutation.mutateAsync(payload);

      queryClient.setQueryData(
        ['unitFloorPlanSchedule', unitId],
        isScheduled ? { scheduledFloorPlanId: values.floorPlanId } : null,
      );

      // Invalidate NAUR caches if NAUR was involved or association was auto-cleared.
      if (shouldInvalidateNaur) {
        queryClient.invalidateQueries(['naurEvents', 'building', buildingId]);
        queryClient.invalidateQueries(['naurEventsForUnit', unitId]);
      }

      // Invalidate the unit query so UnitAffordableSetup reflects the new floor plan.
      queryClient.invalidateQueries(['unit', unitId]);

      invalidateGrossRentsQueries();

      toastr.success(
        intl.formatMessage(appMessages.success),
        intl.formatMessage(
          isScheduled
            ? messages.saveSuccessScheduled
            : messages.saveSuccessImmediate,
        ),
      );
      onSuccess();
    } catch (error) {
      toastr.error(
        intl.formatMessage(appMessages.error),
        intl.formatMessage(messages.saveError),
      );
    }
  };

  const floorPlanOptions = useMemo(
    () =>
      floorPlans.map((floorPlan) => ({
        value: floorPlan.id,
        text: getFloorPlanOptionText(floorPlan),
      })),
    [floorPlans],
  );

  // Effect: Clear and disable NAUR field when unit has association but non-LIHTC floor plan is selected
  useEffect(() => {
    if (!currentlyAssociatedNaurEvent || !selectedFloorPlanId) return;
    if (selectedFloorPlanId === currentFloorPlanId) return;

    const selectedFloorPlan = floorPlans.find(
      (fp) => fp.id === selectedFloorPlanId,
    );
    if (!selectedFloorPlan) return;

    // Check if selected floor plan is NOT LIHTC
    const selectedProgramTypes =
      getAffordableFloorPlanProgramTypes(selectedFloorPlan);
    const isSelectedLIHTC = selectedProgramTypes.isLIHTCFloorPlan;

    // If user selects a non-LIHTC floor plan while unit has a NAUR association,
    // clear the NAUR event selection (set to null/"none")
    if (!isSelectedLIHTC) {
      setValue('naurEventId', null, { shouldDirty: true });
    }
  }, [
    currentlyAssociatedNaurEvent,
    selectedFloorPlanId,
    currentFloorPlanId,
    floorPlans,
    setValue,
  ]);

  useEffect(() => {
    if (!isHUDFloorPlan) return;
    if (!selectedAffordableFundingId) return;
    // Only pre-populate when viewing the unit's current floor plan.
    // If the user has selected a different floor plan, leave the field empty
    // and require an explicit choice.
    if (selectedFloorPlanId !== currentFloorPlanId) return;
    // Skip if the form already has a valid selection the user chose.
    if (
      selectedHudSubsidyTypeId &&
      hudOptions.some((o) => o.value === selectedHudSubsidyTypeId)
    )
      return;
    if (!hudOptions.some((o) => o.value === selectedAffordableFundingId))
      return;
    setValue('hudSubsidyTypeId', selectedAffordableFundingId, {
      shouldDirty: false,
    });
  }, [
    isHUDFloorPlan,
    selectedAffordableFundingId,
    selectedFloorPlanId,
    currentFloorPlanId,
    hudOptions,
    selectedHudSubsidyTypeId,
    setValue,
  ]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={intl.formatMessage(messages.title)}
        actionsProps={[
          {
            children: intl.formatMessage(messages.cancel),
            onClick: handleClose,
            variant: 'text',
          },
          {
            children: intl.formatMessage(messages.save),
            submitButton: true,
            startIcon: <FloppyIcon />,
            disabled: isSaveDisabled || updateUnitMutation.isLoading,
            isSubmitting: updateUnitMutation.isLoading,
            onClick: handleSaveWithConfirmation,
          },
        ]}
        ModalProps={{
          PaperProps: {
            sx: {
              width: {
                xs: 'calc(100% - 32px)',
                sm: 560,
              },
            },
          },
        }}
      >
        <Stack spacing={2}>
          {isLockedByNaur && (
            <Box sx={{ mb: 0 }}>
              <Banner
                status="warning"
                message={intl.formatMessage(naurMessages.naurFloorPlanMessage)}
              />
            </Box>
          )}
          <Typography variant="bodyMedium">
            {intl.formatMessage(messages.descriptionLineOne)}
          </Typography>
          <Box>
            <Controller
              name="floorPlanId"
              control={reactHookFormProps.control}
              render={({ field }) => (
                <SingleSelect
                  {...field}
                  label={intl.formatMessage(messages.floorPlanLabel)}
                  options={floorPlanOptions}
                  value={field.value ?? ''}
                  disabled={isFloorPlansLoading || isLockedByNaur}
                  onChange={(value) => {
                    field.onChange(value || null);
                    setValue('hudSubsidyTypeId', null, { shouldDirty: false });
                  }}
                />
              )}
            />
            {showNaurField && (
              <Box sx={{ mt: 2 }}>
                <Controller
                  name="naurEventId"
                  control={reactHookFormProps.control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label={intl.formatMessage(messages.naurEventLabel)}
                      options={naurEventOptions}
                      value={field.value ?? ''}
                      placeholder={intl.formatMessage(
                        messages.naurEventPlaceholder,
                      )}
                      disabled={
                        !hasSelectableEvents || isBuildingNaurEventsLoading
                      }
                      displayEmpty
                      helperText={
                        !hasSelectableEvents && !isBuildingNaurEventsLoading
                          ? intl.formatMessage(
                              messages.naurEventDisabledTooltip,
                            )
                          : undefined
                      }
                      onChange={(event) => {
                        field.onChange(event.target.value || null);
                      }}
                      onClearSelected={() => field.onChange(null)}
                    />
                  )}
                />
              </Box>
            )}
            {isHUDFloorPlan && (
              <Controller
                name="hudSubsidyTypeId"
                control={reactHookFormProps.control}
                rules={{ required: true }}
                render={({ field }) => (
                  <SingleSelect
                    {...field}
                    label={intl.formatMessage(messages.hudSubsidyType)}
                    options={hudOptions}
                    value={field.value ?? ''}
                    InputLabelProps={{
                      TooltipProps: {
                        title: intl.formatMessage(
                          messages.hudSubsidyTypeTooltip,
                        ),
                        children: <AlertInfoIcon />,
                      },
                    }}
                    helperText={
                      !selectedHudSubsidyTypeId
                        ? intl.formatMessage(appMessages.required)
                        : undefined
                    }
                    onChange={(value) => field.onChange(value || null)}
                  />
                )}
              />
            )}
          </Box>
          <Box>
            <Typography variant="labelLarge">
              {intl.formatMessage(messages.unitStatusLabel)}
            </Typography>
            <Typography variant="bodyMedium" component="div">
              {unitStatusDescription || '---'}
            </Typography>
          </Box>
          {conversionState && (
            <Banner
              status={conversionState.status}
              message={conversionState.message}
            />
          )}
        </Stack>
      </Modal>
      <ModalConfirm
        open={openConfirm}
        onClose={closeConfirmModal}
        title={intl.formatMessage(messages.iaConfirmTitle)}
        actionsProps={[
          {
            children: intl.formatMessage(appMessages.no),
            onClick: closeConfirmModal,
          },
          {
            children: intl.formatMessage(appMessages.yes),
            onClick: handleConfirmSave,
          },
        ]}
        ModalProps={{
          PaperProps: {
            sx: {
              width: {
                xs: 'calc(100% - 32px)',
                sm: 480,
              },
            },
          },
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="bodyMedium">
            {intl.formatMessage(messages.iaConfirmDescription)}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="bodyMedium">
              {intl.formatMessage(messages.iaWithChanges)}
            </Typography>
            <Typography
              variant="bodyMedium"
              color={
                +(projectedIncomeAvg ?? 0) <= +(incomeAveragingPercent || 100)
                  ? 'success.main'
                  : 'error.main'
              }
            >
              {projectedIncomeAvg == null ? '---' : `${projectedIncomeAvg}%`}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="bodyMedium">
              {intl.formatMessage(messages.requiredIa)}
            </Typography>
            <Typography variant="bodyMedium">
              {incomeAveragingPercent == null
                ? '---'
                : `${incomeAveragingPercent}%`}
            </Typography>
          </Stack>
        </Stack>
      </ModalConfirm>
    </>
  );
};

export default ChangeFloorPlanModal;
